import { ref } from 'vue';

const parseBoletoDueOn = value => {
  const dueOn = String(value || '').trim();
  const match = dueOn.match(
    /^(\d{2})\/(\d{2})\/(\d{4})$|^(\d{4})-(\d{2})-(\d{2})$/
  );
  if (!match) return null;

  const [, dayFirst, monthFirst, yearFirst, yearLast, monthLast, dayLast] =
    match;
  const year = Number(yearFirst || yearLast);
  const month = Number(monthFirst || monthLast);
  const day = Number(dayFirst || dayLast);
  const date = new Date(year, month - 1, day, 12);

  return date.getFullYear() === year &&
    date.getMonth() === month - 1 &&
    date.getDate() === day
    ? date
    : null;
};

export const isFinanceBoletoOverdue = (dueOn, today = new Date()) => {
  const dueDate = parseBoletoDueOn(dueOn);
  if (!dueDate) return false;

  const currentDay = new Date(today);
  currentDay.setHours(0, 0, 0, 0);
  return dueDate < currentDay;
};

export function useRealEstateFinancePayments({ finance, onSuggestedStatus }) {
  const boletoFeedbackVisible = ref(false);
  const boletoDialogVisible = ref(false);
  const boletoUploadCount = ref(0);
  const payersDialogVisible = ref(false);
  const paymentDialogVisible = ref(false);
  const selectedResponsibility = ref(null);

  const suggest = status => onSuggestedStatus(status || '');

  const paymentIsOverdue = responsibility => {
    return isFinanceBoletoOverdue(responsibility?.data_validade);
  };

  const syncSelectedResponsibility = responsibilityId => {
    const responsibility =
      finance.selectedInstallment.value?.parcela?.responsaveis_pagamento?.find(
        row => row.id === responsibilityId
      );
    if (responsibility) selectedResponsibility.value = responsibility;
  };

  const handlePayment = async responsibility => {
    if (responsibility.comfirmacao_pagamento === 1) {
      try {
        await finance.confirmPayment(responsibility.id, { confirmed: false });
        suggest(finance.selectedInstallment.value?.sugestao_status);
        return true;
      } catch {
        return false;
      }
    }

    if (!paymentIsOverdue(responsibility)) {
      try {
        const result = await finance.confirmPayment(responsibility.id, {
          confirmed: true,
        });
        suggest(result?.suggested_status);
        return true;
      } catch {
        return false;
      }
    }

    selectedResponsibility.value = responsibility;
    paymentDialogVisible.value = true;
    return true;
  };

  const confirmPayment = async payload => {
    try {
      const result = await finance.confirmPayment(
        selectedResponsibility.value.id,
        payload
      );
      suggest(result?.suggested_status);
      paymentDialogVisible.value = false;
      return true;
    } catch {
      return false;
    }
  };

  const openBoleto = responsibility => {
    boletoFeedbackVisible.value = false;
    boletoUploadCount.value = 0;
    selectedResponsibility.value = responsibility;
    boletoDialogVisible.value = true;
  };

  const closeBoletoFeedback = () => {
    boletoFeedbackVisible.value = false;
    boletoDialogVisible.value = false;
    boletoUploadCount.value = 0;
  };

  const savePayers = async responsibilities => {
    try {
      await finance.updatePaymentResponsibilities(responsibilities);
      payersDialogVisible.value = false;
      return true;
    } catch {
      return false;
    }
  };

  const uploadBoleto = async file => {
    try {
      const responsibilityId = selectedResponsibility.value.id;
      await finance.uploadBoleto(responsibilityId, file);
      syncSelectedResponsibility(responsibilityId);
      boletoUploadCount.value = 1;
      boletoFeedbackVisible.value = true;
      return true;
    } catch {
      return false;
    }
  };

  const saveBoleto = async payload => {
    try {
      const responsibilityId = selectedResponsibility.value.id;
      await finance.updateBoleto(responsibilityId, payload);
      syncSelectedResponsibility(responsibilityId);
      return true;
    } catch {
      return false;
    }
  };

  const removeBoleto = async () => {
    try {
      const responsibilityId = selectedResponsibility.value.id;
      await finance.removeBoleto(responsibilityId);
      syncSelectedResponsibility(responsibilityId);
      return true;
    } catch {
      return false;
    }
  };

  const downloadBoleto = () =>
    finance.downloadBoleto(selectedResponsibility.value.id);

  return {
    boletoFeedbackVisible,
    boletoDialogVisible,
    boletoUploadCount,
    closeBoletoFeedback,
    payersDialogVisible,
    paymentDialogVisible,
    selectedResponsibility,
    handlePayment,
    confirmPayment,
    openBoleto,
    savePayers,
    uploadBoleto,
    saveBoleto,
    removeBoleto,
    downloadBoleto,
  };
}
